import "../../../i18n/config";
import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Checkbox } from "@whatslly/ui-components/v2/Checkbox";
import { Message } from "../Message";
import styles from "./messages.module.scss";
import type { ComponentMessageType } from "../../types/ComponentMessageType";
import { formatMessageItems } from "./utils/formatMessageItems";
import { messagesModals } from "../../stores/messagesModals";
import type { LoginProviderType } from "@whatslly/types/LoginProviderType";
import { ModalProviderInline } from "@whatslly/ui-components/modals";
import { Virtuoso, VirtuosoHandle } from "react-virtuoso";
import { action, makeObservable, observable } from "mobx";
import { useLocalStoreWithInjectedDependencies } from "@whatslly/commons/di";
import { SELECTORS } from "@whatslly/ui-components/Pages/TuvisMessaging/services/selectors";
import classnames from "classnames";
import { observer } from "mobx-react-lite";
import type { MessageFloatingActionType } from "../Message/Components/FloatingActions";
import { MessageInlineActionType } from "../Message/Components/InlineActions";

function isElementWithClassList(target: EventTarget | null): target is Element {
  return target !== null && "classList" in target;
}
const removeAnimationClass = (event: Event) => {
  isElementWithClassList(event.target) && event.target.classList.remove(styles.highlighted);
};

const List = React.forwardRef<HTMLDivElement>((props, ref) => {
  return <div {...SELECTORS.MESSAGES_LIST} {...props} ref={ref} className={styles.elementsList} />;
});

const messageAnimationHandler = (element: Element | null) => {
  if (!element) return;
  element.classList.add(styles.highlighted);
  element.addEventListener("animationend", removeAnimationClass, { once: true });
};

class HighlightHandler {
  shouldHighlightMessage = false;
  constructor() {
    makeObservable(this, { shouldHighlightMessage: observable, startListenToScrolling: action, finishToScroll: action });
  }

  startListenToScrolling() {
    this.shouldHighlightMessage = true;
  }

  finishToScroll() {
    this.shouldHighlightMessage = false;
  }
}

export const Messages = observer<
  {
    items: ComponentMessageType[];
    tempDeletedMessages: Set<string>;
    isSelectMessagesMode: boolean;
    selectedMessages: Set<string>;
    searchText: string;
    onChangeSelectMessageCheckbox?: (id: string) => void;
    highlightedMsgId: string | undefined;
    getEntityUrl?: (type: string, id: string) => string;
    crm: LoginProviderType;
    setShowScrollDown?: (show: boolean) => void;
    setHighlightedMessageId?: (msgId: string) => void;
    startChat?: (data: { firstName: string; lastName: string; phone: string }) => void;
    noMessageText?: React.ReactNode;
    floatingActions?: [MessageFloatingActionType] | [MessageFloatingActionType, MessageFloatingActionType];
    editReaction?: (messageId: string) => void;
    inlineActions?: MessageInlineActionType[];
    disableActions?: boolean;
    hideMessageAck?: boolean;
    reSendMessage?: (msgId: string | undefined) => void;
    reSendingIds?: string[];
    checkIsFraudMessage?: (msgId: string) => void;
    handleMessageUnMount?: (msgId: string) => void;
  },
  { scrollBottom: () => void }
>(
  React.forwardRef(
    (
      {
        items,
        tempDeletedMessages,
        selectedMessages,
        isSelectMessagesMode,
        searchText,
        onChangeSelectMessageCheckbox,
        highlightedMsgId,
        getEntityUrl,
        startChat,
        crm,
        setShowScrollDown,
        setHighlightedMessageId,
        noMessageText,
        floatingActions,
        editReaction,
        inlineActions,
        disableActions,
        hideMessageAck,
        reSendMessage,
        reSendingIds,
        checkIsFraudMessage,
        handleMessageUnMount,
      },
      ref
    ) => {
      const { t } = useTranslation();
      const messagesRef = useRef<HTMLDivElement>(null);
      const virtuosoRef = useRef<VirtuosoHandle>(null);
      const highlightHandler = useLocalStoreWithInjectedDependencies(HighlightHandler);

      const itemsToRender = useMemo(() => {
        return formatMessageItems(items.filter((item) => !tempDeletedMessages.has(item.id)));
      }, [items, tempDeletedMessages]);

      const highlightMessage = useCallback(
        (messageId?: string) => {
          const highlightedMsgIndex = itemsToRender.findIndex((message) => message.type !== "day" && message.msgId === messageId);
          if (highlightedMsgIndex !== -1) {
            highlightHandler.startListenToScrolling();
            virtuosoRef.current?.scrollToIndex({ index: highlightedMsgIndex, behavior: `smooth` });
            setHighlightedMessageId?.(messageId || "");
          }
        },
        [highlightHandler, itemsToRender, setHighlightedMessageId]
      );

      useEffect(() => {
        if (!messagesRef.current) return;
        highlightMessage(highlightedMsgId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [highlightedMsgId]);

      useImperativeHandle(
        ref,
        () => ({
          scrollBottom: (behavior: "smooth" | "auto" = "smooth") => {
            virtuosoRef.current?.scrollToIndex({ index: itemsToRender.length - 1, behavior });
          },
        }),
        [itemsToRender.length]
      );

      return (
        <>
          <ModalProviderInline modalService={messagesModals} />
          {items.length === 0 && (
            <div className={styles.noMessages} data-testid={"no-messages"}>
              {noMessageText ? (
                noMessageText
              ) : (
                <>
                  <div>{t("chat_component.no_message_1")}</div>
                  <div>{t("chat_component.no_message_2")}</div>
                </>
              )}
            </div>
          )}
          {items.length > 0 && itemsToRender.length > 0 && (
            <div ref={messagesRef} style={{ height: "100%", width: "100%" }}>
              <Virtuoso
                ref={virtuosoRef}
                totalCount={itemsToRender.length}
                className={styles.itemsList}
                components={{ List }}
                increaseViewportBy={1000}
                atBottomStateChange={(atBottom) => {
                  setShowScrollDown?.(!atBottom);
                }}
                initialTopMostItemIndex={itemsToRender.length - 1}
                isScrolling={(isScrolling) => {
                  if (!messagesRef.current) return;
                  const message = messagesRef.current.querySelector(`[data-msg-id='${highlightedMsgId}']`);
                  if (!isScrolling && highlightHandler.shouldHighlightMessage && message) {
                    messageAnimationHandler(message);
                    highlightHandler.finishToScroll();
                  }
                }}
                itemContent={(index) => {
                  const vitiosoItem = itemsToRender[index];
                  const vitiosoItemKey = vitiosoItem.type === "message" ? vitiosoItem.id : vitiosoItem.date;
                  return (
                    <React.Fragment key={vitiosoItemKey}>
                      {vitiosoItem.type === "message" ? (
                        <div
                          key={vitiosoItem.id}
                          className={classnames(
                            styles.messageRowContainer,
                            styles.messageRow,
                            vitiosoItem.isInternal ? styles.messageInternalContainer : vitiosoItem.isSent ? styles.messageOutContainer : styles.messageInContainer
                          )}
                          {...{ [SELECTORS.MESSAGE_ID_PROP]: vitiosoItem.id }}
                        >
                          {isSelectMessagesMode && (
                            <Checkbox
                              testId={`message-checkbox-${vitiosoItem.id}`}
                              size="large"
                              onChange={() => onChangeSelectMessageCheckbox?.(vitiosoItem.id)}
                              value={selectedMessages.has(vitiosoItem.id)}
                              classNameContainer={styles.deleteCheckBox}
                            />
                          )}
                          <Message
                            messages={items}
                            highlightMessage={highlightMessage}
                            crm={crm}
                            message={vitiosoItem}
                            searchText={searchText}
                            getEntityUrl={getEntityUrl}
                            startChat={startChat}
                            floatingActions={floatingActions}
                            editReaction={editReaction}
                            inlineActions={inlineActions}
                            disableActions={disableActions}
                            hideMessageAck={hideMessageAck}
                            reSendMessage={reSendMessage}
                            reSendingIds={reSendingIds}
                            checkIsFraudMessage={checkIsFraudMessage}
                            handleMessageUnMount={handleMessageUnMount}
                          />
                        </div>
                      ) : (
                        <div
                          data-testid="date-container"
                          className={classnames(styles.messageDateContainer, styles.messageRow)}
                          key={vitiosoItem.date}
                        >
                          <div className={styles.messageDate}>{vitiosoItem.date}</div>
                        </div>
                      )}
                    </React.Fragment>
                  );
                }}
              />
            </div>
          )}
        </>
      );
    }
  )
);
